import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, CloudUpload, Delete, LocalShipping } from "@mui/icons-material";
import { Checkbox, FormControlLabel } from "@mui/material";
import * as XLSX from "xlsx";
import {
  ComsCatalogItem,
  ComsDailyReport,
  ComsMatDelivery,
  useCreateComsCatalogItemMutation,
  useCreateComsMatDeliveryMutation,
  useDeleteComsCatalogItemMutation,
  useDeleteComsMatDeliveryMutation,
  useGetComsCatalogQuery,
  useGetComsDailyReportsQuery,
  useGetComsMatDeliveriesQuery,
  useUpdateComsCatalogItemMutation,
} from "../../redux/comsApi";

const COMS_NAVY = "#123456";
const MAT_BLUE = "#1565c0";

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmt = (v: number) =>
  "$" +
  (v ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtQty = (v: number) =>
  (v ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ─── CSV helpers ──────────────────────────────────────────────────────────────
function parseCsvRow(line: string): string[] {
  const result: string[] = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else inQ = !inQ;
    } else if (ch === "," && !inQ) {
      result.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  result.push(cur);
  return result;
}

interface MaterialCsvItem {
  section: string;
  description: string;
  unit: string;
  quantity: number;
  rate: number;
  displayOrder: number; // global order across all sections
}

function parseMaterialsCsv(text: string): {
  items: MaterialCsvItem[];
  sectionOrder: string[];
} {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) throw new Error("Empty CSV");
  const header = parseCsvRow(lines[0]);
  const idx = {
    row_type: header.indexOf("row_type"),
    section: header.indexOf("section"),
    description: header.indexOf("description"),
    unit: header.indexOf("unit"),
    quantity: header.indexOf("quantity"),
    rate: header.indexOf("rate"),
  };
  if (idx.row_type < 0) throw new Error("Missing row_type column");
  if (idx.section < 0 || idx.description < 0)
    throw new Error("Missing section or description column");

  const sectionOrder: string[] = [];
  const items: MaterialCsvItem[] = [];
  let globalOrder = 0;

  for (let i = 1; i < lines.length; i++) {
    const row = parseCsvRow(lines[i]);
    const rt = row[idx.row_type]?.trim();
    if (rt === "SECTION_HEADER") {
      const s = row[idx.section]?.trim();
      if (s && !sectionOrder.includes(s)) sectionOrder.push(s);
    } else if (rt === "ITEM") {
      const section = row[idx.section]?.trim() || "General";
      if (!sectionOrder.includes(section)) sectionOrder.push(section);
      items.push({
        section,
        description: row[idx.description]?.trim() || "",
        unit: idx.unit >= 0 ? row[idx.unit]?.trim() || "" : "",
        quantity: idx.quantity >= 0 ? parseFloat(row[idx.quantity]) || 0 : 0,
        rate: idx.rate >= 0 ? parseFloat(row[idx.rate]) || 0 : 0,
        displayOrder: globalOrder++,
      });
    }
  }
  if (!items.length) throw new Error("No ITEM rows found");
  return { items, sectionOrder };
}

// ─── XLSX opening-balance parser ──────────────────────────────────────────────
interface XlsxRowData {
  receivedQty: number;
  orderNo: string;
  usedQty: number;
}

function parseOpeningBalanceXlsx(
  file: File,
): Promise<Record<string, XlsxRowData[]>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const result: Record<string, XlsxRowData[]> = {};

        for (const sheetName of wb.SheetNames) {
          const ws = wb.Sheets[sheetName];
          const rows: any[][] = XLSX.utils.sheet_to_json(ws, {
            header: 1,
            defval: null,
          });
          if (rows.length < 2) continue;

          // Scan ALL rows to find the actual header row (one that has both
          // "received" and "used" column labels). This skips title/note rows.
          let headerRowIdx = -1;
          let receivedCol = -1,
            orderCol = -1,
            usedCol = -1;

          for (let r = 0; r < rows.length; r++) {
            const row = rows[r] as any[];
            let localReceived = -1,
              localOrder = -1,
              localUsed = -1;
            for (let c = 0; c < row.length; c++) {
              const h = String(row[c] ?? "")
                .toLowerCase()
                .trim();
              if (localReceived < 0 && h.includes("receiv")) localReceived = c;
              if (localOrder < 0 && h.includes("order")) localOrder = c;
              if (localUsed < 0 && h.includes("used")) localUsed = c;
            }
            if (localReceived >= 0 && localUsed >= 0) {
              headerRowIdx = r;
              receivedCol = localReceived;
              orderCol = localOrder >= 0 ? localOrder : localReceived + 1;
              usedCol = localUsed;
              break;
            }
          }

          // Fallback: assume the XLSX uses the known column layout
          // (No., Description, Unit, Budgeted Qty, Rate, Budgeted Amt,
          //  Received to Date, Order No, Used to Date, Cost to Date, Left on Site)
          if (headerRowIdx < 0) {
            headerRowIdx = 0;
            receivedCol = 6;
            orderCol = 7;
            usedCol = 8;
          }

          const dataStartRow = headerRowIdx + 1;
          const items: XlsxRowData[] = [];
          for (let r = dataStartRow; r < rows.length; r++) {
            const row = rows[r] as any[];
            if (!row || row.every((v) => v === null || v === "")) continue;
            items.push({
              receivedQty: parseFloat(String(row[receivedCol] ?? 0)) || 0,
              orderNo:
                row[orderCol] != null ? String(row[orderCol]).trim() : "",
              usedQty: parseFloat(String(row[usedCol] ?? 0)) || 0,
            });
          }
          result[sheetName] = items;
        }
        resolve(result);
      } catch (err: any) {
        reject(new Error(`Failed to parse XLSX: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read XLSX file"));
    reader.readAsArrayBuffer(file);
  });
}

// Match a CSV section name to an XLSX sheet name (fuzzy, case-insensitive)
function sectionsMatch(csvSection: string, sheetName: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const a = norm(csvSection);
  const b = norm(sheetName);
  return a === b || a.includes(b) || b.includes(a);
}

// ─── Import dialog ────────────────────────────────────────────────────────────
interface ImportDialogProps {
  blockId: string;
  open: boolean;
  onClose: () => void;
  existingItems: ComsCatalogItem[];
}

const ImportMaterialsDialog: React.FC<ImportDialogProps> = ({
  blockId,
  open,
  onClose,
  existingItems = [],
}) => {
  const existingCount = existingItems.length;
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [xlsxFile, setXlsxFile] = useState<File | null>(null);
  const [openingDate, setOpeningDate] = useState("2026-01-01");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [progressPct, setProgressPct] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const csvRef = useRef<HTMLInputElement>(null);
  const xlsxRef = useRef<HTMLInputElement>(null);

  const [createItem] = useCreateComsCatalogItemMutation();
  const [updateItem] = useUpdateComsCatalogItemMutation();
  const [createDelivery] = useCreateComsMatDeliveryMutation();
  const [deleteItem] = useDeleteComsCatalogItemMutation();

  const reset = () => {
    setCsvFile(null);
    setXlsxFile(null);
    setStatus(null);
    setProgressPct(0);
    setIsRunning(false);
    setReplaceExisting(false);
  };

  const doImport = async () => {
    if (!csvFile) return;
    setIsRunning(true);
    try {
      // Step 0: delete existing items if replacing
      if (replaceExisting && existingItems.length > 0) {
        setStatus(`Clearing ${existingItems.length} existing items…`);
        setProgressPct(2);
        for (const item of existingItems) {
          if (item.id) await deleteItem(item.id).unwrap();
        }
      }

      setStatus("Parsing CSV…");
      setProgressPct(5);
      const text = await csvFile.text();
      const { items } = parseMaterialsCsv(text);

      let openingData: Record<string, XlsxRowData[]> = {};
      if (xlsxFile) {
        setStatus("Parsing XLSX…");
        setProgressPct(10);
        openingData = await parseOpeningBalanceXlsx(xlsxFile);
      }

      // Track created items per section for XLSX positional matching
      const sectionCreated: Record<string, { id: string; section: string }[]> =
        {};

      const total = items.length;
      let done = 0;

      for (const item of items) {
        const result = await createItem({
          blockId,
          type: "material",
          name: item.description,
          unit: item.unit || "—",
          defaultRate: item.rate,
          plannedQty: item.quantity,
          displayOrder: item.displayOrder,
          section: item.section,
          openingActualQty: 0,
        }).unwrap();

        if (!sectionCreated[item.section]) sectionCreated[item.section] = [];
        sectionCreated[item.section].push({
          id: result.id!,
          section: item.section,
        });

        done++;
        setProgressPct(10 + Math.round((done / total) * 60));
        setStatus(`Importing ${done} / ${total} items…`);
      }

      // Apply XLSX opening balances
      let openingCount = 0;
      if (xlsxFile && Object.keys(openingData).length > 0) {
        setStatus("Applying opening balances…");
        setProgressPct(75);

        for (const [sheetName, rows] of Object.entries(openingData)) {
          const matchedSection = Object.keys(sectionCreated).find((s) =>
            sectionsMatch(s, sheetName),
          );
          if (!matchedSection) continue;

          const itemList = sectionCreated[matchedSection];
          for (let i = 0; i < Math.min(itemList.length, rows.length); i++) {
            const row = rows[i];
            const itemId = itemList[i].id;

            // Store opening used qty on the catalog item
            if (row.usedQty > 0) {
              await updateItem({
                id: itemId,
                data: { openingActualQty: row.usedQty },
              }).unwrap();
            }

            // Create opening delivery entry
            if (row.receivedQty > 0) {
              await createDelivery({
                blockId,
                budgetItemId: itemId,
                deliveryDate: openingDate,
                orderNo: row.orderNo || "Opening Balance",
                qtyReceived: row.receivedQty,
                isOpeningBalance: true,
              }).unwrap();
              openingCount++;
            }
          }
        }
      }

      setProgressPct(100);
      setStatus(
        xlsxFile
          ? `Done — ${done} materials imported, ${openingCount} opening-balance entries created.`
          : `Done — ${done} materials imported.`,
      );
      setTimeout(() => {
        reset();
        onClose();
      }, 2000);
    } catch (err: any) {
      const msg =
        err?.data?.error?.message ??
        err?.data?.message ??
        err?.message ??
        (err?.status ? `HTTP ${err.status}` : JSON.stringify(err));
      setStatus(`Error: ${msg}`);
      setProgressPct(0);
      setIsRunning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isRunning) {
          reset();
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: MAT_BLUE, color: "#fff" }}>
        Import Materials Catalog
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {existingCount > 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            This block already has {existingCount} material items.
          </Alert>
        )}
        {existingCount > 0 && (
          <FormControlLabel
            sx={{ mb: 2, ml: 0 }}
            control={
              <Checkbox
                size="small"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                disabled={isRunning}
                color="error"
              />
            }
            label={
              <Typography variant="body2" color="error.main">
                Delete all existing materials first, then re-import (recommended
                after a failed import)
              </Typography>
            }
          />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Import the materials budget catalog from a CSV. Only{" "}
          <strong>ITEM</strong> rows are imported;{" "}
          <strong>SECTION_HEADER</strong> rows define section grouping and
          order.
        </Typography>

        {/* CSV */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} mb={0.5}>
            Materials CSV{" "}
            <Typography component="span" color="error.main">
              *
            </Typography>
          </Typography>
          <input
            ref={csvRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloudUpload />}
            onClick={() => csvRef.current?.click()}
            disabled={isRunning}
          >
            {csvFile ? csvFile.name : "Choose CSV…"}
          </Button>
        </Box>

        {/* XLSX */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} mb={0.5}>
            Opening Balance XLSX{" "}
            <Typography component="span" color="text.secondary">
              (optional)
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={0.5}
          >
            One sheet per section, same item order as the CSV. Columns: Received
            to Date, Order No, Used to Date.
          </Typography>
          <input
            ref={xlsxRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={(e) => setXlsxFile(e.target.files?.[0] ?? null)}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloudUpload />}
            onClick={() => xlsxRef.current?.click()}
            disabled={isRunning}
          >
            {xlsxFile ? xlsxFile.name : "Choose XLSX…"}
          </Button>
        </Box>

        {/* Opening date */}
        {xlsxFile && (
          <TextField
            label="Opening Balance Date"
            type="date"
            size="small"
            value={openingDate}
            onChange={(e) => setOpeningDate(e.target.value)}
            disabled={isRunning}
            sx={{ mb: 2, width: 200 }}
            InputLabelProps={{ shrink: true }}
          />
        )}

        {/* Progress */}
        {isRunning && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{ mb: 1 }}
            />
          </Box>
        )}
        {status && (
          <Typography
            variant="body2"
            color={
              status.startsWith("Error")
                ? "error.main"
                : status.startsWith("Done")
                ? "success.main"
                : "text.secondary"
            }
          >
            {status}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => {
            reset();
            onClose();
          }}
          disabled={isRunning}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!csvFile || isRunning}
          onClick={doImport}
          sx={{ bgcolor: MAT_BLUE, "&:hover": { bgcolor: "#0d47a1" } }}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Delivery log dialog ──────────────────────────────────────────────────────
interface DeliveryLogDialogProps {
  blockId: string;
  item: ComsCatalogItem;
  open: boolean;
  onClose: () => void;
}

const DeliveryLogDialog: React.FC<DeliveryLogDialogProps> = ({
  blockId,
  item,
  open,
  onClose,
}) => {
  const { data: deliveries = [], isLoading } = useGetComsMatDeliveriesQuery(
    { blockId, budgetItemId: item.id! },
    { skip: !item.id },
  );
  const [createDelivery, { isLoading: adding }] =
    useCreateComsMatDeliveryMutation();
  const [deleteDelivery, { isLoading: deleting }] =
    useDeleteComsMatDeliveryMutation();

  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));
  const [newOrderNo, setNewOrderNo] = useState("");
  const [newQty, setNewQty] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const handleAdd = async () => {
    setAddError(null);
    const qty = parseFloat(newQty);
    if (!newDate) {
      setAddError("Date is required.");
      return;
    }
    if (isNaN(qty) || qty <= 0) {
      setAddError("Quantity must be a positive number.");
      return;
    }
    await createDelivery({
      blockId,
      budgetItemId: item.id!,
      deliveryDate: newDate,
      orderNo: newOrderNo.trim(),
      qtyReceived: qty,
      isOpeningBalance: false,
    }).unwrap();
    setNewDate(new Date().toISOString().slice(0, 10));
    setNewOrderNo("");
    setNewQty("");
  };

  const totalReceived = (deliveries as ComsMatDelivery[]).reduce(
    (s, d) => s + d.qtyReceived,
    0,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: MAT_BLUE, color: "#fff" }}>
        Delivery Log — {item.name}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Unit: <strong>{item.unit || "—"}</strong> &nbsp;|&nbsp; Received to
          Date: <strong>{fmtQty(totalReceived)}</strong>
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : (deliveries as ComsMatDelivery[]).length === 0 ? (
          <Typography variant="body2" color="text.secondary" mb={2}>
            No deliveries logged yet.
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#e8edf5" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                  Order No
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    textAlign: "right",
                  }}
                >
                  Qty Received
                </TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(deliveries as ComsMatDelivery[]).map((d) => (
                <TableRow key={d.id}>
                  <TableCell sx={{ fontSize: "0.8rem" }}>
                    {d.deliveryDate}
                    {d.isOpeningBalance && (
                      <Chip
                        label="Opening"
                        size="small"
                        sx={{
                          ml: 0.5,
                          height: 16,
                          fontSize: "0.65rem",
                          bgcolor: "#e3f2fd",
                          color: MAT_BLUE,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.8rem" }}>
                    {d.orderNo || "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.8rem", textAlign: "right" }}>
                    {fmtQty(d.qtyReceived)}
                  </TableCell>
                  <TableCell sx={{ p: 0 }}>
                    <IconButton
                      size="small"
                      disabled={deleting}
                      onClick={() => deleteDelivery(d.id!)}
                    >
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Add new delivery */}
        <Typography variant="body2" fontWeight={700} mb={1} color={MAT_BLUE}>
          Log New Delivery
        </Typography>
        <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
          <TextField
            label="Date"
            type="date"
            size="small"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            label="Order No"
            size="small"
            value={newOrderNo}
            onChange={(e) => setNewOrderNo(e.target.value)}
            sx={{ width: 140 }}
          />
          <TextField
            label="Qty Received"
            size="small"
            type="number"
            value={newQty}
            onChange={(e) => setNewQty(e.target.value)}
            inputProps={{ min: 0, step: "any" }}
            sx={{ width: 130 }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={handleAdd}
            disabled={adding}
            sx={{
              bgcolor: MAT_BLUE,
              "&:hover": { bgcolor: "#0d47a1" },
              mt: 0.25,
            }}
          >
            Add
          </Button>
        </Box>
        {addError && (
          <Typography variant="caption" color="error.main" mt={0.5}>
            {addError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Enriched item type ───────────────────────────────────────────────────────
interface EnrichedMatItem extends ComsCatalogItem {
  usedQty: number;
  receivedQty: number;
  budgetedAmt: number;
  costToDate: number;
  leftOnSite: number;
}

// ─── Main component ───────────────────────────────────────────────────────────
interface Props {
  blockId: string;
}

const MaterialsBudget: React.FC<Props> = ({ blockId }) => {
  const { data: catalogItems = [], isLoading: loadingCatalog } =
    useGetComsCatalogQuery({ blockId, type: "material" });
  const { data: reports = [], isLoading: loadingReports } =
    useGetComsDailyReportsQuery(blockId);
  const { data: deliveries = [], isLoading: loadingDeliveries } =
    useGetComsMatDeliveriesQuery({ blockId });
  const [updateItem] = useUpdateComsCatalogItemMutation();

  const [importOpen, setImportOpen] = useState(false);
  const [deliveryItem, setDeliveryItem] = useState<ComsCatalogItem | null>(
    null,
  );

  // Inline unit-price editing state
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPriceVal, setEditingPriceVal] = useState("");

  // D3: Inline budget qty editing state
  const [editingBudgetQtyId, setEditingBudgetQtyId] = useState<string | null>(null);
  const [editingBudgetQtyVal, setEditingBudgetQtyVal] = useState("");

  // Build used-from-daily-reports map: catalogItemId / name → total quantity
  const dailyUsedMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of reports as ComsDailyReport[]) {
      for (const mat of (r.materials ?? []) as any[]) {
        const key = (mat.catalogItemId ?? mat.name) as string;
        if (key) m.set(key, (m.get(key) ?? 0) + (Number(mat.quantity) || 0));
      }
    }
    return m;
  }, [reports]);

  const recordedMaterialsActualTotal = useMemo(() => {
    let total = 0;
    for (const r of reports as ComsDailyReport[]) {
      for (const mat of (r.materials ?? []) as any[]) {
        total += Number(mat.amount) || 0;
      }
    }
    return total;
  }, [reports]);

  const openingUsageValue = useMemo(
    () =>
      (catalogItems as ComsCatalogItem[]).reduce(
        (sum, item) =>
          sum +
          (Number(item.openingActualQty) || 0) * (Number(item.defaultRate) || 0),
        0,
      ),
    [catalogItems],
  );

  // Build deliveries map: budgetItemId → total received
  const receivedMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of deliveries as ComsMatDelivery[]) {
      m.set(d.budgetItemId, (m.get(d.budgetItemId) ?? 0) + d.qtyReceived);
    }
    return m;
  }, [deliveries]);

  // Enrich items with computed fields
  const enriched = useMemo<EnrichedMatItem[]>(() => {
    return (catalogItems as ComsCatalogItem[]).map((item) => {
      const dailyUsed =
        (item.id ? dailyUsedMap.get(item.id) : undefined) ??
        dailyUsedMap.get(item.name) ??
        0;
      const usedQty = (item.openingActualQty ?? 0) + dailyUsed;
      const receivedQty = item.id ? receivedMap.get(item.id) ?? 0 : 0;
      const unitPrice = item.defaultRate ?? 0;
      const budgetedAmt = (item.plannedQty ?? 0) * unitPrice;
      const costToDate = usedQty * unitPrice;
      const leftOnSite = receivedQty - usedQty;
      return {
        ...item,
        usedQty,
        receivedQty,
        budgetedAmt,
        costToDate,
        leftOnSite,
      };
    });
  }, [catalogItems, dailyUsedMap, receivedMap]);

  // Group by section, preserving CSV section order (via global displayOrder)
  const { sections, grandBudgeted, grandCost } = useMemo(() => {
    const sorted = [...enriched].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
    );
    const sectionOrder: string[] = [];
    const sectionMap = new Map<string, EnrichedMatItem[]>();
    for (const item of sorted) {
      const sec = item.section ?? "General";
      if (!sectionMap.has(sec)) {
        sectionMap.set(sec, []);
        sectionOrder.push(sec);
      }
      sectionMap.get(sec)!.push(item);
    }
    let grandBudgeted = 0,
      grandCost = 0;
    const sections = sectionOrder.map((name) => {
      const items = sectionMap.get(name)!;
      const sectionBudgeted = items.reduce((s, i) => s + i.budgetedAmt, 0);
      const sectionCost = items.reduce((s, i) => s + i.costToDate, 0);
      grandBudgeted += sectionBudgeted;
      grandCost += sectionCost;
      return { name, items, sectionBudgeted, sectionCost };
    });
    return { sections, grandBudgeted, grandCost };
  }, [enriched]);

  // Price cell edit handlers
  const startPriceEdit = (item: EnrichedMatItem) => {
    setEditingPriceId(item.id ?? null);
    setEditingPriceVal(String(item.defaultRate ?? 0));
  };
  const commitPriceEdit = async (item: EnrichedMatItem) => {
    if (!item.id) return;
    const val = parseFloat(editingPriceVal) || 0;
    setEditingPriceId(null);
    if (val !== (item.defaultRate ?? 0)) {
      await updateItem({ id: item.id, data: { defaultRate: val } });
    }
  };

  // D3: Budget qty edit handlers
  const startBudgetQtyEdit = (item: EnrichedMatItem) => {
    setEditingBudgetQtyId(item.id ?? null);
    setEditingBudgetQtyVal(String(item.plannedQty ?? 0));
  };
  const commitBudgetQtyEdit = async (item: EnrichedMatItem) => {
    if (!item.id) return;
    const val = parseFloat(editingBudgetQtyVal) || 0;
    setEditingBudgetQtyId(null);
    if (val !== (item.plannedQty ?? 0)) {
      await updateItem({ id: item.id, data: { plannedQty: val } });
    }
  };

  // Cost-to-date cell style
  const costStyle = (cost: number, budget: number) => {
    if (budget <= 0) return {};
    if (cost > budget)
      return { bgcolor: "#ffebee", color: "#c62828", fontWeight: 700 };
    if (cost > budget * 0.9)
      return { bgcolor: "#fff8e1", color: "#e65100", fontWeight: 600 };
    return {};
  };

  const isLoading = loadingCatalog || loadingReports || loadingDeliveries;

  // Table header cells
  const hd = {
    fontWeight: 700,
    fontSize: "0.73rem",
    color: "#fff",
    bgcolor: MAT_BLUE,
    whiteSpace: "nowrap" as const,
    py: 1,
  };
  const rHd = { ...hd, textAlign: "right" as const };
  const num = { textAlign: "right" as const };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h2" sx={{ color: COMS_NAVY, fontWeight: 600 }}>
          Materials — Budget &amp; Usage
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CloudUpload />}
          onClick={() => setImportOpen(true)}
          sx={{ borderColor: MAT_BLUE, color: MAT_BLUE }}
        >
          Import Catalog
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Actual Cost on the Overview card uses recorded daily report amounts plus
        any Opening Actual Cost entered on the block. This table&apos;s Cost to Date
        is a catalog valuation based on `(opening actual qty + daily used qty) ×
        current unit price`, so it can be higher than AC.
      </Alert>

      {!isLoading && (
        <Box display="flex" flexWrap="wrap" gap={1.5} mb={2}>
          <Chip
            label={`Recorded Materials Cost (feeds AC): ${fmt(recordedMaterialsActualTotal)}`}
            sx={{ bgcolor: '#e3f2fd', color: MAT_BLUE, fontWeight: 700 }}
          />
          <Chip
            label={`Opening Usage Valuation: ${fmt(openingUsageValue)}`}
            variant="outlined"
            sx={{ borderColor: '#90caf9', color: MAT_BLUE }}
          />
          <Chip
            label={`Catalog Cost to Date: ${fmt(grandCost)}`}
            variant="outlined"
            sx={{ borderColor: '#90caf9', color: MAT_BLUE }}
          />
        </Box>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (catalogItems as ComsCatalogItem[]).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography color="text.secondary" mb={2}>
            No materials catalog found for this block.
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setImportOpen(true)}
            sx={{ bgcolor: MAT_BLUE }}
          >
            Import Materials Catalog
          </Button>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, overflowX: "auto" }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...hd, minWidth: 220 }}>Description</TableCell>
                <TableCell sx={{ ...hd, textAlign: "center", width: 64 }}>
                  Unit
                </TableCell>
                <TableCell sx={{ ...rHd, width: 90 }}>Budget Qty</TableCell>
                <TableCell sx={{ ...rHd, width: 110 }}>
                  <Tooltip title="Click any price to edit">
                    <span>Unit Price ✎</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...rHd, width: 110 }}>Budget Amt</TableCell>
                <TableCell sx={{ ...rHd, width: 100 }}>Received</TableCell>
                <TableCell sx={{ ...rHd, width: 90 }}>Used to Date</TableCell>
                <TableCell sx={{ ...rHd, width: 110 }}>
                  <Tooltip title="Red = over budget · Orange = &gt;90% of budget">
                    <span>Cost to Date</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...rHd, width: 100 }}>Left on Site</TableCell>
                <TableCell sx={{ ...hd, textAlign: "center", width: 50 }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {sections.map((sec) => (
                <React.Fragment key={sec.name}>
                  {/* Section header */}
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.78rem",
                        color: COMS_NAVY,
                        bgcolor: "#e8edf5",
                        borderLeft: `4px solid ${MAT_BLUE}`,
                        py: 1,
                        letterSpacing: 0.5,
                      }}
                    >
                      {sec.name}
                    </TableCell>
                  </TableRow>

                  {/* Item rows */}
                  {sec.items.map((item) => (
                    <TableRow
                      key={item.id ?? item.name}
                      sx={{ "&:hover": { bgcolor: "rgba(21,101,192,0.04)" } }}
                    >
                      <TableCell sx={{ fontSize: "0.78rem", py: 0.75, pl: 3 }}>
                        {item.name}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "0.78rem", textAlign: "center" }}
                      >
                        {item.unit || "—"}
                      </TableCell>
                      <TableCell sx={{ ...num, fontSize: "0.78rem", p: 0.5 }}>
                        {/* D3: inline edit for budget qty when it is missing (0 or blank) */}
                        {editingBudgetQtyId === item.id ? (
                          <TextField
                            autoFocus
                            size="small"
                            variant="standard"
                            value={editingBudgetQtyVal}
                            onChange={(e) => setEditingBudgetQtyVal(e.target.value)}
                            onBlur={() => commitBudgetQtyEdit(item)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitBudgetQtyEdit(item);
                              if (e.key === "Escape") setEditingBudgetQtyId(null);
                            }}
                            inputProps={{ style: { textAlign: "right", width: 70 } }}
                          />
                        ) : (
                          <Box
                            onClick={() => startBudgetQtyEdit(item)}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { bgcolor: "rgba(21,101,192,0.1)", borderRadius: 1 },
                              px: 0.5, py: 0.25, borderRadius: 1,
                              border: "1px dashed transparent",
                            }}
                          >
                            {item.plannedQty ? fmtQty(item.plannedQty) : <Typography component="span" sx={{ color: MAT_BLUE, fontSize: "0.7rem" }}>Set qty ✎</Typography>}
                          </Box>
                        )}
                      </TableCell>

                      {/* Unit Price — inline editable */}
                      <TableCell sx={{ ...num, fontSize: "0.78rem", p: 0.5 }}>
                        {editingPriceId === item.id ? (
                          <TextField
                            autoFocus
                            size="small"
                            variant="standard"
                            value={editingPriceVal}
                            onChange={(e) => setEditingPriceVal(e.target.value)}
                            onBlur={() => commitPriceEdit(item)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitPriceEdit(item);
                              if (e.key === "Escape") setEditingPriceId(null);
                            }}
                            inputProps={{
                              style: { textAlign: "right", width: 80 },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }}
                          />
                        ) : (
                          <Box
                            onClick={() => startPriceEdit(item)}
                            sx={{
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: "rgba(21,101,192,0.1)",
                                borderRadius: 1,
                              },
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 1,
                              border: "1px dashed transparent",
                              "&:hover .price-edit-hint": { opacity: 1 },
                            }}
                          >
                            {fmt(item.defaultRate ?? 0)}
                            <Typography
                              component="span"
                              className="price-edit-hint"
                              sx={{
                                opacity: 0,
                                fontSize: "0.65rem",
                                color: MAT_BLUE,
                                ml: 0.5,
                              }}
                            >
                              ✎
                            </Typography>
                          </Box>
                        )}
                      </TableCell>

                      <TableCell sx={{ ...num, fontSize: "0.78rem" }}>
                        {item.budgetedAmt > 0 ? fmt(item.budgetedAmt) : "—"}
                      </TableCell>
                      <TableCell sx={{ ...num, fontSize: "0.78rem" }}>
                        {item.receivedQty > 0 ? fmtQty(item.receivedQty) : "—"}
                      </TableCell>
                      <TableCell sx={{ ...num, fontSize: "0.78rem" }}>
                        {item.usedQty > 0 ? fmtQty(item.usedQty) : "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...num,
                          fontSize: "0.78rem",
                          ...costStyle(item.costToDate, item.budgetedAmt),
                        }}
                      >
                        {item.costToDate > 0 ? (
                          <Tooltip
                            title={
                              item.budgetedAmt > 0 &&
                              item.costToDate > item.budgetedAmt
                                ? `⚠ Over budget by ${fmt(
                                    item.costToDate - item.budgetedAmt,
                                  )}`
                                : ""
                            }
                            disableHoverListener={
                              item.costToDate <= item.budgetedAmt ||
                              item.budgetedAmt === 0
                            }
                          >
                            <span>{fmt(item.costToDate)}</span>
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...num,
                          fontSize: "0.78rem",
                          color: item.leftOnSite < 0 ? "#c62828" : "inherit",
                        }}
                      >
                        {item.receivedQty > 0 ? fmtQty(item.leftOnSite) : "—"}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", p: 0 }}>
                        <Tooltip title="Delivery log">
                          <IconButton
                            size="small"
                            onClick={() => setDeliveryItem(item)}
                            sx={{ color: MAT_BLUE }}
                          >
                            <LocalShipping fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Section subtotal */}
                  <TableRow
                    sx={{
                      bgcolor: "#f0f4f8",
                      borderTop: "2px solid #c5d3e8",
                    }}
                  >
                    <TableCell
                      colSpan={4}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        color: "#555",
                        pl: 3,
                        py: 1,
                      }}
                    >
                      {sec.name} — subtotal
                    </TableCell>
                    <TableCell
                      sx={{
                        ...num,
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: COMS_NAVY,
                      }}
                    >
                      {fmt(sec.sectionBudgeted)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell
                      sx={{
                        ...num,
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        ...costStyle(sec.sectionCost, sec.sectionBudgeted),
                      }}
                    >
                      {fmt(sec.sectionCost)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </React.Fragment>
              ))}

              {/* Grand total */}
              <TableRow sx={{ bgcolor: COMS_NAVY }}>
                <TableCell
                  colSpan={4}
                  sx={{
                    fontWeight: 800,
                    color: "#fff",
                    fontSize: "0.85rem",
                    py: 1.5,
                  }}
                >
                  GRAND TOTAL
                </TableCell>
                <TableCell
                  sx={{
                    ...num,
                    fontWeight: 800,
                    color: "#fff",
                    fontSize: "0.85rem",
                    py: 1.5,
                  }}
                >
                  {fmt(grandBudgeted)}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell
                  sx={{
                    ...num,
                    fontWeight: 800,
                    color: grandCost > grandBudgeted ? "#ffcdd2" : "#a5d6a7",
                    fontSize: "0.85rem",
                    py: 1.5,
                  }}
                >
                  {fmt(grandCost)}
                </TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Import dialog */}
      <ImportMaterialsDialog
        blockId={blockId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        existingItems={catalogItems as ComsCatalogItem[]}
      />

      {/* Delivery log dialog */}
      {deliveryItem && (
        <DeliveryLogDialog
          blockId={blockId}
          item={deliveryItem}
          open={!!deliveryItem}
          onClose={() => setDeliveryItem(null)}
        />
      )}
    </Box>
  );
};

export default MaterialsBudget;
